// src/components/album/GalleryManager.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu,
  ChevronLeft,
  X, 
  Users,
  Briefcase,
  MapPin,
  Maximize2,
  Plus, 
  Upload, 
  Image, 
  ImageIcon,
  Tag,
  ShoppingCart,
  Layout,
  Video, 
  Edit2,
  Trash2, 
  Mic,
  Save,
  Edit, 
  Grid,
  Eye,
  EyeOff,
  Wand2,
  Layers,
  Settings,
  Loader2,
  Palette,
  Sparkles,
  GripVertical,
  BookOpen,
  MousePointer,
  AlertCircle,
  Minus,
  Monitor,
  Tablet,
  Smartphone
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { albumService } from '../../services/albumService';
import { getDefaultSettings } from '../../utils/layoutUtils';
import { MediaService } from '../../services/mediaService';
import { MediaApprovalModal } from './MediaApprovalModal';
import { verticalListSortingStrategy } from '@dnd-kit/sortable';
import { GalleryEditorModal } from './GalleryEditorModal';


// Layout Categories Definition
const LAYOUT_CATEGORIES = {
  grid: {
    label: 'Grid Layouts',
    icon: Grid,
    description: 'Structured, organized presentations',
    layouts: {
      masonry: { 
        name: 'Masonry', 
        description: 'Pinterest-style flowing grid',
        preview: 'masonry-preview'
      },
      uniformGrid: { 
        name: 'Uniform Grid', 
        description: 'Equal-sized cards',
        preview: 'uniform-preview'
      },
      bento: { 
        name: 'Bento Box', 
        description: 'Japanese-inspired varied sizing',
        preview: 'bento-preview'
      }
    }
  },
  narrative: {
    label: 'Storytelling',
    icon: BookOpen,
    description: 'Layouts that tell a story',
    layouts: {
      heroFlow: { 
        name: 'Hero Flow', 
        description: 'Featured content with supporting media',
        preview: 'hero-preview'
      },
      magazineSpread: { 
        name: 'Magazine Spread', 
        description: 'Editorial-style layouts',
        preview: 'magazine-preview'
      },
      timelineFlow: { 
        name: 'Timeline Flow', 
        description: 'Chronological storytelling',
        preview: 'timeline-preview'
      }
    }
  },
  artistic: {
    label: 'Artistic',
    icon: Palette,
    description: 'Creative and dynamic presentations',
    layouts: {
      constellation: { 
        name: 'Constellation', 
        description: 'Organic, star-like positioning',
        preview: 'constellation-preview'
      },
      riverFlow: { 
        name: 'River Flow', 
        description: 'Flowing, water-like curves',
        preview: 'river-preview'
      },
      floatingIslands: { 
        name: 'Floating Islands', 
        description: 'Clustered content',
        preview: 'islands-preview'
      }
    }
  },
  flow: {
    label: 'Flow Layouts',
    description: 'Dynamic flowing layouts', 
    icon: Layers,
    layouts: {
      timeline: {
        name: 'Timeline',
        description: 'Chronological timeline',
        preview: 'timeline-preview'
      }
    }
  },
  // FIX: Convert these to the same structure
  voice: {
    label: 'Voice & Story',
    icon: BookOpen,
    description: 'Narrative-driven layouts with voice',
    layouts: {
      storyteller: {
        name: 'Storyteller',
        description: 'Narrative-driven layout with voice segments',
        preview: 'storyteller-preview',
        voiceMode: 'segments',
        config: {
          showGalleryVoice: true,
          segmentDisplay: 'timeline',
          mediaVoiceOverlay: 'hover',
          autoPlaySegments: false
        },
        styling: {
          voiceIndicator: 'waveform',
          accentColor: 'purple',
          segmentCards: 'elevated'
        }
      },
      heritage: {
        name: 'Heritage Album',
        description: 'Family stories with multi-speaker voices',
        preview: 'heritage-preview',
        voiceMode: 'multi-speaker',
        config: {
          showGalleryVoice: true,
          groupByS: true,
          showSpeakerAvatars: true,
          mediaVoiceOverlay: 'prominent'
        },
        styling: {
          voiceIndicator: 'speaker-badge',
          accentColor: 'amber',
          speakerHighlight: true
        }
      },
      showcase: {
        name: 'Professional Showcase',
        description: 'Portfolio with creator commentary',
        preview: 'showcase-preview',
        voiceMode: 'creator-notes',
        config: {
          showGalleryVoice: true,
          segmentDisplay: 'chapters',
          mediaVoiceOverlay: 'icon',
          transcriptDefault: true
        },
        styling: {
          voiceIndicator: 'minimal',
          accentColor: 'blue',
          professionalMode: true
        }
      },
      journey: {
        name: 'Journey Timeline',
        description: 'Chronological story with voice waypoints',
        preview: 'journey-preview',
        voiceMode: 'timeline',
        config: {
          showGalleryVoice: true,
          segmentDisplay: 'waypoints',
          mediaVoiceOverlay: 'timeline-marker',
          autoPlayOnScroll: true
        },
        styling: {
          voiceIndicator: 'timeline-dot',
          accentColor: 'green',
          animatedProgress: true
        }
      },
      immersive: {
        name: 'Immersive Experience',
        description: 'Full-screen media with ambient voice',
        preview: 'immersive-preview',
        voiceMode: 'ambient',
        config: {
          showGalleryVoice: true,
          segmentDisplay: 'fullscreen',
          mediaVoiceOverlay: 'floating',
          autoPlaySegments: true
        },
        styling: {
          voiceIndicator: 'floating-player',
          accentColor: 'indigo',
          overlayOpacity: 0.9
        }
      }
    }
  }
};

const ALBUM_CATEGORIES = {
  'photo-album': { label: 'Photo Album', icon: ImageIcon, color: '#3b82f6' },
  'teams': { label: 'Team Management', icon: Users, color: '#8b5cf6' },
  'menu': { label: 'Menu', icon: Tag, color: '#f59e0b' },
  'store': { label: 'Store', icon: ShoppingCart, color: '#10b981' },
  'listing': { label: 'Listings', icon: Grid, color: '#06b6d4' },
  'portfolio': { label: 'Portfolio', icon: Palette, color: '#ec4899' },
  'slides': { label: 'Slides', icon: Layout, color: '#6366f1' },
  'students': { label: 'Students', icon: Users, color: '#14b8a6' }
};

const GALLERY_TEMPLATES = {
  custom: {
    name: 'Custom Layout',
    icon: Layers,
    description: 'Single layout for entire gallery',
    isSegmented: false
  },
  modernGrid: {
    name: 'Modern Grid',
    icon: Grid,
    description: 'Clean, uniform cards with hover effects',
    isSegmented: true,
    defaultSegments: [
      { id: 's1', name: 'Hero Section', layout: 'heroFlow', items: 1 },
      { id: 's2', name: 'Main Gallery', layout: 'uniformGrid', items: 6 },
      { id: 's3', name: 'Featured', layout: 'bento', items: 4 }
    ]
  },
  magazine: {
    name: 'Magazine',
    icon: BookOpen,
    description: 'Editorial-style with varied sizing',
    isSegmented: true,
    defaultSegments: [
      { id: 's1', name: 'Cover Spread', layout: 'magazineSpread', items: 2 },
      { id: 's2', name: 'Article Grid', layout: 'masonry', items: 6 },
      { id: 's3', name: 'Feature Story', layout: 'heroFlow', items: 3 }
    ]
  },
  cinematic: {
    name: 'Cinematic',
    icon: Maximize2,
    description: 'Dramatic full-width presentations',
    isSegmented: true,
    defaultSegments: [
      { id: 's1', name: 'Hero Banner', layout: 'fullWidth', items: 1 },
      { id: 's2', name: 'Dual Feature', layout: 'sideBySide', items: 2 },
      { id: 's3', name: 'Gallery Strip', layout: 'horizontalScroll', items: 5 }
    ]
  },
  mosaic: {
    name: 'Mosaic',
    icon: Sparkles,
    description: 'Dynamic mixed layouts',
    isSegmented: true,
    defaultSegments: [
      { id: 's1', name: 'Bento Grid', layout: 'bento', items: 4 },
      { id: 's2', name: 'Hero Moment', layout: 'heroFlow', items: 2 },
      { id: 's3', name: 'Masonry Wall', layout: 'masonry', items: 6 },
      { id: 's4', name: 'Uniform Cards', layout: 'uniformGrid', items: 4 }
    ]
  }
};

const SEGMENT_LAYOUTS = {
  masonry: { name: 'Masonry', columns: 3, gap: 16 },
  uniformGrid: { name: 'Uniform Grid', columns: 3, gap: 16, aspectRatio: '1:1' },
  bento: { name: 'Bento Box', columns: 4, gap: 12 },
  heroFlow: { name: 'Hero Flow', columns: 1, gap: 24 },
  magazineSpread: { name: 'Magazine Spread', columns: 2, gap: 20 },
  fullWidth: { name: 'Full Width', columns: 1, gap: 0 },
  sideBySide: { name: 'Side by Side', columns: 2, gap: 24 },
  horizontalScroll: { name: 'Horizontal Scroll', columns: 5, gap: 16 }
};

// CREATE NEW COMPONENT at top after imports (around line 66)
const MediaEditForm = ({ mediaItem, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: mediaItem.title || '',
    description: mediaItem.description || '',
    metadata: {
      location: mediaItem.metadata?.location || '',
      date: mediaItem.metadata?.date || '',
      tags: mediaItem.metadata?.tags || []
    }
  });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.metadata.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          tags: [...prev.metadata.tags, tagInput.trim()]
        }
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        tags: prev.metadata.tags.filter(t => t !== tag)
      }
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter description"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
        <input
          type="text"
          value={formData.metadata.location}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            metadata: { ...prev.metadata, location: e.target.value }
          }))}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter location"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
        <input
          type="date"
          value={formData.metadata.date}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            metadata: { ...prev.metadata, date: e.target.value }
          }))}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add tag"
          />
          <button
            type="button"
            onClick={addTag}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.metadata.tags.map((tag, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="hover:text-blue-900"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t">
        <button
          type="submit"
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
        >
          {saving ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Save Changes
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-6 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Simple Layout Preview Component
const SimpleLayoutPreview = ({ layoutType }) => {
  const previewStyles = {
    masonry: (
      <div className="grid grid-cols-3 gap-1 h-16">
        <div className="bg-blue-200 rounded h-8" />
        <div className="bg-purple-200 rounded h-6" />
        <div className="bg-green-200 rounded h-10" />
        <div className="bg-orange-200 rounded h-7" />
        <div className="bg-pink-200 rounded h-9" />
        <div className="bg-indigo-200 rounded h-6" />
      </div>
    ),
    uniformGrid: (
      <div className="grid grid-cols-3 gap-1 h-16">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-blue-200 rounded aspect-square" />
        ))}
      </div>
    ),
    bento: (
      <div className="grid grid-cols-4 grid-rows-3 gap-1 h-16">
        <div className="bg-purple-300 rounded col-span-2 row-span-2" />
        <div className="bg-blue-200 rounded" />
        <div className="bg-green-200 rounded" />
        <div className="bg-orange-200 rounded" />
        <div className="bg-pink-200 rounded" />
        <div className="bg-indigo-200 rounded col-span-2" />
      </div>
    ),
    heroFlow: (
      <div className="h-16 flex flex-col gap-1">
        <div className="bg-gradient-to-r from-purple-300 to-blue-300 rounded h-10" />
        <div className="grid grid-cols-3 gap-1 flex-1">
          <div className="bg-blue-200 rounded" />
          <div className="bg-green-200 rounded" />
          <div className="bg-orange-200 rounded" />
        </div>
      </div>
    ),
    constellation: (
      <div className="relative h-16">
        <div className="absolute w-3 h-3 bg-blue-300 rounded-full" style={{ left: '20%', top: '30%' }} />
        <div className="absolute w-2 h-2 bg-green-300 rounded-full" style={{ left: '60%', top: '20%' }} />
        <div className="absolute w-2.5 h-2.5 bg-purple-300 rounded-full" style={{ left: '40%', top: '60%' }} />
        <div className="absolute w-2 h-2 bg-orange-300 rounded-full" style={{ left: '80%', top: '50%' }} />
      </div>
    )
  };

  return (
    <div className="w-full h-16 p-2 bg-gray-50 rounded">
      {previewStyles[layoutType] || previewStyles.masonry}
    </div>
  );
};

const SortableSegment = ({ segment, onUpdate, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: segment.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-lg border border-gray-200 p-3 mb-2">
      <div className="flex items-center gap-2 mb-3">
        <div {...attributes} {...listeners} className="cursor-grab hover:bg-gray-100 p-1.5 rounded">
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>
        
        <input
          type="text"
          value={segment.name}
          onChange={(e) => onUpdate(segment.id, 'name', e.target.value)}
          className="flex-1 text-sm font-medium border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
        />

        <select
          value={segment.layout}
          onChange={(e) => onUpdate(segment.id, 'layout', e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1"
        >
          {Object.entries(SEGMENT_LAYOUTS).map(([key, layout]) => (
            <option key={key} value={key}>{layout.name}</option>
          ))}
        </select>

        <input
          type="number"
          min="1"
          max="20"
          value={segment.items}
          onChange={(e) => onUpdate(segment.id, 'items', parseInt(e.target.value))}
          className="w-14 text-xs border border-gray-300 rounded px-2 py-1 text-center"
        />

        <button
          onClick={() => onDelete(segment.id)}
          className="p-1.5 hover:bg-red-50 rounded text-red-600"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-gray-50 rounded p-2">
        <div className={`grid gap-1.5 ${
          segment.layout === 'horizontalScroll' ? 'grid-flow-col auto-cols-fr overflow-x-auto' :
          segment.layout === 'fullWidth' ? 'grid-cols-1' :
          segment.layout === 'sideBySide' ? 'grid-cols-2' :
          segment.layout === 'bento' ? 'grid-cols-4' :
          'grid-cols-3'
        }`}>
          {Array.from({ length: Math.min(segment.items, 6) }).map((_, i) => (
            <div
              key={i}
              className={`bg-gradient-to-br from-blue-400 to-purple-500 rounded ${
                segment.layout === 'bento' && i === 0 ? 'col-span-2 row-span-2 h-16' :
                segment.layout === 'heroFlow' ? 'h-12' :
                segment.layout === 'fullWidth' ? 'h-16' :
                'h-8'
              }`}
            />
          ))}
          {segment.items > 6 && (
            <div className="h-8 bg-gray-300 rounded flex items-center justify-center text-xs text-gray-600">
              +{segment.items - 6}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Layout Settings Modal Component 
const GalleryLayoutModal = ({ 
  gallery, 
  layoutConfig, 
  onLayoutChange, 
  onSave, 
  onClose 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState(layoutConfig?.template || 'custom');
  const [segments, setSegments] = useState(layoutConfig?.segments || []);
  const [activeCategory, setActiveCategory] = useState(layoutConfig?.category || 'grid');
  const [activeLayout, setActiveLayout] = useState(layoutConfig?.layout || 'masonry');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [settings, setSettings] = useState(layoutConfig?.settings || {});
  const [isSaving, setIsSaving] = useState(false);

  const currentLayoutInfo = LAYOUT_CATEGORIES[activeCategory]?.layouts[activeLayout];
  const isSegmented = GALLERY_TEMPLATES[selectedTemplate]?.isSegmented;

  const segmentSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleTemplateChange = (templateKey) => {
    setSelectedTemplate(templateKey);
    const template = GALLERY_TEMPLATES[templateKey];
    
    if (template.isSegmented && template.defaultSegments) {
      setSegments(template.defaultSegments);
    } else {
      setSegments([]);
    }
  };

  const handleSegmentUpdate = (segmentId, field, value) => {
    setSegments(prev => prev.map(s => 
      s.id === segmentId ? { ...s, [field]: value } : s
    ));
  };

  const handleAddSegment = () => {
    const newSegment = {
      id: `s${Date.now()}`,
      name: `Segment ${segments.length + 1}`,
      layout: 'masonry',
      items: 4
    };
    setSegments(prev => [...prev, newSegment]);
  };

  const handleDeleteSegment = (segmentId) => {
    if (segments.length > 1) {
      setSegments(prev => prev.filter(s => s.id !== segmentId));
    }
  };

  const handleSegmentDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSegments((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    const categoryData = LAYOUT_CATEGORIES[category];
    if (!categoryData || !categoryData.layouts) return;
    
    const layoutKeys = Object.keys(categoryData.layouts);
    if (layoutKeys.length === 0) return;
    
    const firstLayout = layoutKeys[0];
    setActiveLayout(firstLayout);
    const defaultSettings = getDefaultSettings(category, firstLayout);
    setSettings(defaultSettings);
  };

  const handleLayoutChange = (layout) => {
    setActiveLayout(layout);
    const defaultSettings = getDefaultSettings(activeCategory, layout);
    setSettings(defaultSettings);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const finalConfig = {
        template: selectedTemplate,
        segments: isSegmented ? segments : [],
        category: activeCategory,
        layout: activeLayout,
        settings,
        customizations: layoutConfig?.customizations || {},
        lastUpdated: new Date()
      };
      await onSave(finalConfig);
    } catch (error) {
      console.error('Error saving layout:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-7xl max-h-screen flex flex-col overflow-hidden rounded-xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold">Gallery Layout Builder</h2>
            <p className="text-blue-100 text-sm mt-1">{gallery.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" disabled={isSaving}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-72 border-r border-gray-200 p-4 overflow-y-auto flex-shrink-0 bg-gray-50">
            {/* Template Selection */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">Gallery Template</h3>
              <div className="space-y-2">
                {Object.entries(GALLERY_TEMPLATES).map(([key, template]) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => handleTemplateChange(key)}
                      className={`w-full p-3 rounded-lg text-left transition-all border-2 ${
                        selectedTemplate === key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-4 h-4 ${selectedTemplate === key ? 'text-blue-600' : 'text-gray-600'}`} />
                        <span className="font-medium text-sm">{template.name}</span>
                      </div>
                      <p className="text-xs text-gray-500">{template.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Segments (if segmented template) */}
            {isSegmented && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Segments</h3>
                  <button
                    onClick={handleAddSegment}
                    className="p-1.5 hover:bg-blue-50 rounded text-blue-600"
                    title="Add Segment"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <DndContext sensors={segmentSensors} collisionDetection={closestCenter} onDragEnd={handleSegmentDragEnd}>
                  <SortableContext items={segments.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    {segments.map((segment) => (
                      <SortableSegment
                        key={segment.id}
                        segment={segment}
                        onUpdate={handleSegmentUpdate}
                        onDelete={handleDeleteSegment}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}

            {/* Layout Categories (for custom template) */}
            {!isSegmented && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">Layout Style</h3>
                <div className="space-y-2">
                  {Object.entries(LAYOUT_CATEGORIES).map(([key, category]) => {
                    const IconComponent = category.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => handleCategoryChange(key)}
                        className={`w-full p-3 rounded-lg text-left transition-colors ${
                          activeCategory === key
                            ? 'bg-blue-50 border-2 border-blue-200 text-blue-700'
                            : 'hover:bg-gray-100 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <IconComponent className="h-4 w-4" />
                          <div className="text-sm font-medium">{category.label}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {!isSegmented && (
              <>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {LAYOUT_CATEGORIES[activeCategory].label}
                  </h3>
                  <p className="text-gray-600">{LAYOUT_CATEGORIES[activeCategory].description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {Object.entries(LAYOUT_CATEGORIES[activeCategory].layouts).map(([key, layout]) => (
                    <button
                      key={key}
                      onClick={() => handleLayoutChange(key)}
                      className={`p-4 border-2 rounded-xl text-left transition-all ${
                        activeLayout === key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <SimpleLayoutPreview layoutType={key} />
                      <h4 className="font-semibold text-gray-900 mb-1 mt-3">{layout.name}</h4>
                      <p className="text-sm text-gray-600">{layout.description}</p>
                    </button>
                  ))}
                </div>

                {currentLayoutInfo && (
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Settings className="h-5 w-5" />
                      {currentLayoutInfo.name} Settings
                    </h4>
                    <BasicLayoutSettings
                      layoutType={activeLayout}
                      settings={settings}
                      onChange={setSettings}
                    />
                  </div>
                )}
              </>
            )}

            {isSegmented && (
              <div className="text-center py-12">
                <Sparkles className="w-16 h-16 mx-auto text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {GALLERY_TEMPLATES[selectedTemplate].name} Template
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Configure your segments in the sidebar. Each segment can have its own layout style and number of items.
                </p>
              </div>
            )}
          </div>

          {/* Preview Sidebar */}
          <div className="w-80 border-l border-gray-200 p-4 overflow-y-auto flex-shrink-0 bg-gray-50">
            <div className="mb-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Preview
              </h4>
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setPreviewMode('mobile')}
                  className={`flex-1 p-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                    previewMode === 'mobile' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600'
                  }`}
                >
                  <Smartphone className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPreviewMode('tablet')}
                  className={`flex-1 p-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                    previewMode === 'tablet' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600'
                  }`}
                >
                  <Tablet className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPreviewMode('desktop')}
                  className={`flex-1 p-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                    previewMode === 'desktop' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600'
                  }`}
                >
                  <Monitor className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
              {isSegmented ? (
                <div className="space-y-2">
                  {segments.slice(0, 3).map(segment => (
                    <div key={segment.id} className="border border-gray-200 rounded p-2">
                      <div className="text-xs font-medium text-gray-600 mb-1">{segment.name}</div>
                      <SimpleLayoutPreview layoutType={segment.layout} />
                    </div>
                  ))}
                </div>
              ) : (
                <SimpleLayoutPreview layoutType={activeLayout} />
              )}
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h5 className="text-sm font-semibold text-blue-900 mb-2">Configuration</h5>
              <div className="text-xs text-blue-700 space-y-1">
                <p><strong>Template:</strong> {GALLERY_TEMPLATES[selectedTemplate].name}</p>
                {isSegmented && <p><strong>Segments:</strong> {segments.length}</p>}
                {!isSegmented && (
                  <>
                    <p><strong>Category:</strong> {LAYOUT_CATEGORIES[activeCategory]?.label}</p>
                    <p><strong>Layout:</strong> {currentLayoutInfo?.name}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-white flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{gallery.name}</span>
            <span className="mx-2">•</span>
            <span>{gallery.mediaItems?.length || 0} items</span>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Layout
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Basic Layout Settings Component
const BasicLayoutSettings = ({ layoutType, settings, onChange }) => {
  const updateSetting = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  switch (layoutType) {
    case 'masonry':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Gutter Size</label>
            <input
              type="range"
              min="8"
              max="32"
              value={settings.gutter || 16}
              onChange={(e) => updateSetting('gutter', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-sm text-gray-500">{settings.gutter || 16}px</span>
          </div>
        </div>
      );
    
    case 'uniformGrid':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Aspect Ratio</label>
            <select
              value={settings.aspectRatio || '1:1'}
              onChange={(e) => updateSetting('aspectRatio', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="1:1">Square (1:1)</option>
              <option value="4:3">Landscape (4:3)</option>
              <option value="16:9">Widescreen (16:9)</option>
            </select>
          </div>
        </div>
      );
    
    default:
      return (
        <div className="text-center py-4 text-gray-500">
          <p>Basic settings for {layoutType}</p>
        </div>
      );
  }
};

const DroppableGalleryArea = ({ gallery, children, isActive }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `gallery-${gallery.id}`,
    data: { type: 'gallery', galleryId: gallery.id }
  });

  return (
    <div
      ref={setNodeRef}
      className={`relative transition-all ${isOver ? 'ring-4 ring-blue-400 ring-offset-2' : ''} ${isActive ? 'min-h-[200px]' : ''}`}
    >
      {children}
      {isOver && (
        <div className="absolute inset-0 bg-blue-100 bg-opacity-30 rounded-lg flex items-center justify-center pointer-events-none z-10">
          <div className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg">
            Drop here to move to {gallery.name}
          </div>
        </div>
      )}
    </div>
  );
};

// Sortable Media Item Component
const SortableMediaItem = ({ mediaItem, bookId, selectedGallery, albumService, setGalleries, setSelectedGallery, 
  setReplacingMedia, setShowReplaceModal
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: mediaItem.id,
    data: { 
      type: 'media',
      mediaItem,
      sourceGalleryId: selectedGallery.id 
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative bg-gray-100 rounded-lg overflow-hidden aspect-square group transition-opacity onClose{
        mediaItem.visible === false ? 'opacity-60' : ''
      }`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 z-20 p-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-md cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4 text-gray-700" />
      </div>

      {/* Media Content */}
      {mediaItem.type === 'youtube' ? (
        // YouTube Thumbnail with Play Button
        <div className="relative w-full h-full">
          <img
            src={mediaItem.thumbnailUrl || `https://img.youtube.com/vi/onClose{mediaItem.videoId}/maxresdefault.jpg`}
            alt={mediaItem.title || 'YouTube video'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
          {/* YouTube Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
              <div className="w-0 h-0 border-l-8 border-l-white border-y-6 border-y-transparent ml-1" />
            </div>
          </div>
          {/* YouTube Badge */}
          <div className="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-xs rounded font-medium flex items-center gap-1">
            <Video className="h-3 w-3" />
            YouTube
          </div>
        </div>
      ) : mediaItem.type === 'image' ? (
        <img
          src={mediaItem.url}
          alt={mediaItem.title || 'Media item'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
        />
      ) : (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
          <Video className="h-8 w-8 text-gray-400" />
        </div>
      )}
      
      {/* Hidden Badge - Shows when media is hidden */}
      {mediaItem.visible === false && (
        <div className="absolute top-2 left-2 z-10">
          <span className="bg-gray-900 bg-opacity-80 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <EyeOff className="h-3 w-3" />
            Hidden
          </span>
        </div>
      )}
      
      {/* Action Buttons Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <div className="flex gap-2">
          {/* Visibility Toggle Button */}
          <button 
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const newVisibility = mediaItem.visible === false ? true : false;
                await albumService.toggleMediaVisibility(
                  bookId, 
                  selectedGallery.id, 
                  mediaItem.id, 
                  newVisibility
                );
                
                // Update local state
                setGalleries(prev => prev.map(gallery => ({
                  ...gallery,
                  mediaItems: gallery.mediaItems.map(item =>
                    item.id === mediaItem.id 
                      ? { ...item, visible: newVisibility } 
                      : item
                  )
                })));
                
                // Update selectedGallery
                setSelectedGallery(prev => ({
                  ...prev,
                  mediaItems: prev.mediaItems.map(item =>
                    item.id === mediaItem.id 
                      ? { ...item, visible: newVisibility } 
                      : item
                  )
                }));
                
                console.log(`Media onClose{newVisibility ? 'shown' : 'hidden'} successfully`);
              } catch (error) {
                console.error('Error toggling visibility:', error);
                alert('Failed to update visibility');
              }
            }}
            className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
            title={mediaItem.visible === false ? 'Show' : 'Hide'}
          >
            {mediaItem.visible === false ? (
              <Eye className="h-4 w-4 text-gray-700" />
            ) : (
              <EyeOff className="h-4 w-4 text-gray-700" />
            )}
          </button>
          
          {/* Edit Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setReplacingMedia(mediaItem);
                  setShowReplaceModal(true);
                }}
                className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                title="Edit"
              >
                <Edit className="h-4 w-4 text-blue-600" />
              </button>
          
          {/* Delete Button */}
          <button
            onClick={async (e) => {
              e.stopPropagation();
              if (!window.confirm('Are you sure you want to delete this media item? This action cannot be undone.')) {
                return;
              }

              try {
                await albumService.deleteMedia(bookId, selectedGallery.id, mediaItem.id);
                
                // Update local state to remove the deleted item
                setGalleries(prev => prev.map(gallery => ({
                  ...gallery,
                  mediaItems: gallery.mediaItems.filter(item => item.id !== mediaItem.id)
                })));
                
                // Update selectedGallery if it's currently selected
                if (selectedGallery?.id === selectedGallery.id) {
                  setSelectedGallery(prev => ({
                    ...prev,
                    mediaItems: prev.mediaItems.filter(item => item.id !== mediaItem.id)
                  }));
                }
                
                console.log('Media deleted successfully');
              } catch (error) {
                console.error('Error deleting media:', error);
                alert('Failed to delete media. Please try again.');
              }
            }}
            className="p-2 bg-white rounded-lg hover:bg-red-100 transition-colors"
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Sortable Gallery Item Component
const SortableGalleryItem = ({
  gallery,
  selectedGallery,
  editingGalleryId,
  editingGalleryName,
  setEditingGalleryName,
  setEditingGalleryId,
  setSelectedGallery,
  saveGalleryName,
  openLayoutSettings,
  updateGalleryVoicePermissions,
  bookData
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: gallery.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="group">
      <button
        onClick={() => setSelectedGallery(gallery)}
        className={`w-full p-3 rounded-lg text-left transition-colors ${
          selectedGallery?.id === gallery.id
            ? 'bg-pink-50 border border-pink-200 text-pink-700'
            : 'hover:bg-gray-50 border border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-4 w-4 text-gray-400" />
            </div>
            
            <div className="flex-1">
              {editingGalleryId === gallery.id ? (
                <input
                  type="text"
                  value={editingGalleryName}
                  onChange={(e) => setEditingGalleryName(e.target.value)}
                  onBlur={() => saveGalleryName(gallery.id, editingGalleryName)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveGalleryName(gallery.id, editingGalleryName);
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setEditingGalleryId(null);
                      setEditingGalleryName('');
                    }
                  }}
                  className="font-medium bg-white border border-pink-500 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div 
                  className="font-medium cursor-pointer hover:text-pink-600 hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingGalleryId(gallery.id);
                    setEditingGalleryName(gallery.name);
                  }}
                >
                  {gallery.name}
                </div>
              )}
              <div className="text-sm text-gray-500">
                {gallery.mediaItems?.length || 0} items
              </div>

              {/* Show category badge */}
              {gallery.categoryConfig?.category && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                    {ALBUM_CATEGORIES[gallery.categoryConfig.category]?.label || gallery.categoryConfig.category}
                  </span>
                  {gallery.categoryConfig.category === 'teams' && gallery.categoryConfig.subcategory && (
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                      {gallery.categoryConfig.subcategory}
                    </span>
                  )}
                </div>
              )}
              <div className="text-xs text-blue-600 mt-1">
                {LAYOUT_CATEGORIES[gallery.layoutConfig?.category || 'grid']?.layouts[gallery.layoutConfig?.layout || 'masonry']?.name || 'Masonry'}
              </div>
            </div>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                openLayoutSettings(gallery);
              }}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-all"
              title="Configure Layout"
            >
              <Wand2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </button>

      {bookData.voiceSettings?.galleryNarration?.enabled && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-end">
            <div className="flex items-center gap-3">
              <Mic className="w-4 h-4 text-blue-600" />
            </div>
            <select
              value={gallery.voicePermissions?.canRecord || bookData.voiceSettings?.galleryNarration?.permissions?.canRecord || 'owner-only'}
              onChange={(e) => updateGalleryVoicePermissions(gallery.id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="text-xs px-6 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            >
              <option value="owner-only">Owner Only</option>
              <option value="collaborators">Collaborators</option>
              <option value="authenticated">Signed-in</option>
              <option value="anyone">Anyone</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// Main GalleryManager Component
export const GalleryManager = ({ bookId, user, bookData, isOpen, onClose, onUpdate }) => {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [newGalleryName, setNewGalleryName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [editingGalleryId, setEditingGalleryId] = useState(null);
  const [editingGalleryName, setEditingGalleryName] = useState('');
  const [showHidden, setShowHidden] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const [editingMedia, setEditingMedia] = useState(null);
  const [showMediaEditor, setShowMediaEditor] = useState(false);

  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [addingYoutubeVideo, setAddingYoutubeVideo] = useState(false);

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const [activeGalleryId, setActiveGalleryId] = useState(null);

  const [showGalleryEditor, setShowGalleryEditor] = useState(false);
  const [editingGallerySettings, setEditingGallerySettings] = useState(null);
  const [replacingMedia, setReplacingMedia] = useState(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replacingFile, setReplacingFile] = useState(null);
  const [replacingProgress, setReplacingProgress] = useState(0);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dropZoneRef = useRef(null);

  const [layoutConfig, setLayoutConfig] = useState({
    category: 'grid',
    layout: 'masonry',
    settings: {},
    customizations: {}
  });

  useEffect(() => {
    if (isOpen) {
      loadGalleries();
    }
  }, [isOpen, bookId]);

  const loadGalleries = async () => {
    try {
      const galleriesData = await albumService.getBookGalleries(bookId);
      setGalleries(galleriesData);
      if (galleriesData.length > 0 && !selectedGallery) {
        setSelectedGallery(galleriesData[0]);
      }
    } catch (error) {
      console.error('Error loading galleries:', error);
    }
  };

  const createGallery = async () => {
    if (!newGalleryName.trim()) return;

    try {
      const galleryData = {
        name: newGalleryName.trim(),
        description: '',
        order: galleries.length,
        isPublic: true,
        mediaCount: 0,
        layoutConfig: {
          category: 'grid',
          layout: 'masonry',
          settings: getDefaultSettings('grid', 'masonry'),
          customizations: {},
          lastUpdated: new Date()
        },
        // ADD THIS: Initialize with album category as default
        categoryConfig: {
          category: bookData?.albumSettings?.albumCategory || 'photo-album',
          subcategory: bookData?.albumSettings?.albumSubcategory || null,
          // Default settings that can be overridden in editor
          columnsBreakpoints: { mobile: 1, tablet: 2, desktop: 3, wide: 4 },
          cardStyle: 'default',
          showTitles: true,
          showDescriptions: true,
          imageAspectRatio: 'auto',
          enableLikes: true,
          enableComments: true,
          enableSharing: true,
          enableReactions: false,
          enableVoiceNotes: false,
          enableBooking: false,
          bookingUrl: '',
          enableShopping: false,
          shoppingUrl: '',
          galleryVisibility: 'public',
          allowedRoles: [],
          requireAuth: false,
          allowContributions: false,
          contributorApproval: 'auto',
          lastUpdated: new Date().toISOString()
        }
      };

      await albumService.createGallery(bookId, galleryData);
      
      setNewGalleryName('');
      setShowCreateGallery(false);
      loadGalleries();
    } catch (error) {
      console.error('Error creating gallery:', error);
    }
  };

  const updateGalleryLayout = async (galleryId, newLayoutConfig) => {
    try {
      await albumService.updateGalleryLayout(bookId, galleryId, newLayoutConfig);
      loadGalleries();
    } catch (error) {
      console.error('Error updating gallery layout:', error);
    }
  };

  const openLayoutSettings = (gallery) => {
    setEditingGallery(gallery);
    setLayoutConfig(gallery.layoutConfig || {
      category: 'grid',
      layout: 'masonry',
      settings: getDefaultSettings('grid', 'masonry'),
      customizations: {}
    });
    setShowLayoutSettings(true);
  };

  const saveGalleryName = async (galleryId, newName) => {
    if (!newName.trim()) {
      setEditingGalleryId(null);
      return;
    }
    
    try {
      await albumService.updateGallery(bookId, galleryId, { name: newName.trim() });
      setEditingGalleryId(null);
      setEditingGalleryName('');
      loadGalleries(); // Refresh the list
    } catch (error) {
      console.error('Error updating gallery name:', error);
      alert('Failed to update gallery name');
      setEditingGalleryId(null);
    }
  };
  
  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (e.dataTransfer.types.includes('Files')) {
        setIsDraggingOver(true);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
        setIsDraggingOver(false);
      }
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(false);

      if (!selectedGallery) {
        alert('Please select a gallery first');
        return;
      }

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        await handleFileUpload(selectedGallery.id, files);
      }
    };

    const dropZone = dropZoneRef.current;
    if (dropZone) {
      dropZone.addEventListener('dragenter', handleDragEnter);
      dropZone.addEventListener('dragover', handleDragOver);
      dropZone.addEventListener('dragleave', handleDragLeave);
      dropZone.addEventListener('drop', handleDrop);
    }

    return () => {
      if (dropZone) {
        dropZone.removeEventListener('dragenter', handleDragEnter);
        dropZone.removeEventListener('dragover', handleDragOver);
        dropZone.removeEventListener('dragleave', handleDragLeave);
        dropZone.removeEventListener('drop', handleDrop);
      }
    };
  }, [selectedGallery]);

  const handleFileUpload = async (galleryId, files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const fileArray = Array.from(files);
      await albumService.uploadMediaFiles(bookId, galleryId, fileArray, (current, total) => {
        setUploadProgress(Math.round((current / total) * 100));
      });
      
      loadGalleries();
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error uploading files. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteMedia = async (galleryId, mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media item? This action cannot be undone.')) {
      return;
    }

    try {
      await albumService.deleteMedia(bookId, galleryId, mediaId);
      
      // Update local state to remove the deleted item
      setGalleries(prev => prev.map(gallery => ({
        ...gallery,
        mediaItems: gallery.mediaItems.filter(item => item.id !== mediaId)
      })));
      
      // Update selectedGallery if it's currently selected
      if (selectedGallery?.id === galleryId) {
        setSelectedGallery(prev => ({
          ...prev,
          mediaItems: prev.mediaItems.filter(item => item.id !== mediaId)
        }));
      }
      
      console.log('Media deleted successfully');
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Failed to delete media. Please try again.');
    }
  };

  const handleAddYoutubeVideo = async () => {
    if (!youtubeUrl.trim() || !selectedGallery) return;

    setAddingYoutubeVideo(true);
    try {
      // Detect if it's a valid YouTube URL
      const mediaType = MediaService.detectMediaType(youtubeUrl);
      
      if (mediaType !== 'youtube') {
        alert('Please enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=...)');
        return;
      }

      // Extract video ID and metadata
      const videoId = MediaService.getYouTubeVideoId(youtubeUrl);
      if (!videoId) {
        alert('Could not extract video ID from YouTube URL');
        return;
      }

      const metadata = MediaService.getMediaMetadata(youtubeUrl, 'youtube');

      // Prepare YouTube data object
      const youtubeData = {
        url: youtubeUrl,
        videoId: videoId,
        title: metadata.title || 'YouTube Video',
        description: '',
        thumbnailUrl: metadata.thumbnail,
        metadata: {
          source: 'youtube',
          embedUrl: `https://www.youtube.com/embed/onClose{videoId}`,
          addedAt: new Date().toISOString()
        }
      };

      // Use the new albumService method
      const result = await albumService.addYoutubeVideo(
        bookId, 
        selectedGallery.id, 
        youtubeData
      );
      
      console.log('✅ YouTube video added:', result);
      
      // Clear input and reload galleries
      setYoutubeUrl('');
      await loadGalleries();
      
      // Optional: Show success message
      // You could add a toast notification here
      
    } catch (error) {
      console.error('Error adding YouTube video:', error);
      alert(`Failed to add YouTube video: onClose{error.message}`);
    } finally {
      setAddingYoutubeVideo(false);
    }
  };

  const countPendingMedia = () => {
    let count = 0;
    galleries.forEach(gallery => {
      const pending = (gallery.mediaItems || []).filter(
        item => item.status === 'pending' && item.uploadedBy
      );
      count += pending.length;
    });
    setPendingCount(count);
  };

  // ADD useEffect to count pending media when galleries change
  useEffect(() => {
    countPendingMedia();
  }, [galleries]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    if (active.data.current?.type === 'media' && over.data.current?.type === 'gallery') {
      const sourceGalleryId = active.data.current.sourceGalleryId;
      const targetGalleryId = over.data.current.galleryId;
      const mediaItem = active.data.current.mediaItem;

      if (sourceGalleryId !== targetGalleryId) {
        try {
          await albumService.moveMediaBetweenGalleries(bookId, sourceGalleryId, targetGalleryId, mediaItem.id);

          const targetGallery = galleries.find(g => g.id === targetGalleryId);
          
          setGalleries(prev => prev.map(g => {
            if (g.id === sourceGalleryId) {
              return { ...g, mediaItems: g.mediaItems.filter(item => item.id !== mediaItem.id) };
            }
            if (g.id === targetGalleryId) {
              return { ...g, mediaItems: [...(g.mediaItems || []), mediaItem] };
            }
            return g;
          }));

          if (selectedGallery?.id === sourceGalleryId) {
            setSelectedGallery(prev => ({
              ...prev,
              mediaItems: prev.mediaItems.filter(item => item.id !== mediaItem.id)
            }));
          }

          alert(`Moved to ${targetGallery?.name}`);
          return;
        } catch (error) {
          console.error('Error:', error);
          alert('Failed to move');
          return;
        }
      }
    }

    const oldIndex = displayedMediaItems.findIndex(item => item.id === active.id);
    const newIndex = displayedMediaItems.findIndex(item => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update UI
    const newMediaItems = arrayMove(displayedMediaItems, oldIndex, newIndex);
    
    setSelectedGallery(prev => ({
      ...prev,
      mediaItems: newMediaItems
    }));

    setGalleries(prev => prev.map(gallery => 
      gallery.id === selectedGallery.id
        ? { ...gallery, mediaItems: newMediaItems }
        : gallery
    ));

    // Update Firestore
    try {
      await albumService.reorderGalleryMedia(bookId, selectedGallery.id, newMediaItems);
      console.log('✅ Media reordered successfully');
    } catch (error) {
      console.error('❌ Error reordering media:', error);
      // Revert on error
      loadGalleries();
      alert('Failed to reorder media. Please try again.');
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const toggleMediaVisibility = async (galleryId, mediaId, currentVisibility) => {
    try {
      const newVisibility = !currentVisibility;
      await albumService.toggleMediaVisibility(bookId, galleryId, mediaId, newVisibility);
      
      // Update local state
      setGalleries(prev => prev.map(gallery => ({
        ...gallery,
        mediaItems: gallery.mediaItems.map(item =>
          item.id === mediaId ? { ...item, visible: newVisibility } : item
        )
      })));
      
      // Optional: Show notification
      console.log(`Media ${newVisibility ? 'shown' : 'hidden'} successfully`);
    } catch (error) {
      console.error('Error toggling visibility:', error);
      alert('Failed to update visibility');
    }
  };

  const updateGalleryVoicePermissions = async (galleryId, permissionLevel) => {
    try {
      await albumService.updateGalleryVoicePermissions(bookId, galleryId, {
        canRecord: permissionLevel,
        requireModeration: bookData.voiceSettings?.galleryNarration?.permissions?.requireModeration || false
      });
      
      // Reload galleries to reflect changes
      await loadGalleries();
      
      console.log('✅ Gallery voice permissions updated');
    } catch (error) {
      console.error('Error updating gallery voice permissions:', error);
      alert('Failed to update permissions');
    }
  };

  const gallerySensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleGalleryDragStart = (event) => {
    setActiveGalleryId(event.active.id);
  };

  const handleGalleryDragEnd = async (event) => {
    const { active, over } = event;
    
    setActiveGalleryId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = galleries.findIndex(g => g.id === active.id);
    const newIndex = galleries.findIndex(g => g.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedGalleries = arrayMove(galleries, oldIndex, newIndex).map((gallery, index) => ({
      ...gallery,
      order: index
    }));
    
    setGalleries(reorderedGalleries);

    try {
      await albumService.reorderGalleries(bookId, reorderedGalleries);
      console.log('✅ Galleries reordered successfully');
    } catch (error) {
      console.error('❌ Error reordering galleries:', error);
      loadGalleries();
      alert('Failed to reorder galleries. Please try again.');
    }
  };

  const handleGalleryDragCancel = () => {
    setActiveGalleryId(null);
  };

  const openGalleryEditor = (gallery) => {
    setEditingGallerySettings(gallery);
    setShowGalleryEditor(true);
  };

  const handleSaveGallerySettings = async (categoryConfig) => {
    try {
      console.log('Updating gallery:', editingGallerySettings.id);
      console.log('With categoryConfig:', categoryConfig);
      
      if (!bookId || !editingGallerySettings?.id) {
        throw new Error('Missing bookId or galleryId');
      }
      
      await albumService.updateGallery(bookId, editingGallerySettings.id, {
        categoryConfig: categoryConfig
      });
      
      setShowGalleryEditor(false);
      setEditingGallerySettings(null);
      await loadGalleries();
      console.log('✅ Gallery settings saved successfully');
    } catch (error) {
      console.error('❌ Error saving gallery settings:', error);
      alert(`Failed to save: ${error.message}`);
      throw error;
    }
  };

  const handleSaveMediaEdit = async (updatedFields) => {
    try {
      if (!bookId || !selectedGallery?.id || !editingMedia?.id) {
        throw new Error('Missing required IDs');
      }

      await albumService.updateMediaMetadata(
        bookId,
        selectedGallery.id,
        editingMedia.id,
        updatedFields
      );

      // Update local state
      setGalleries(prev => prev.map(gallery => 
        gallery.id === selectedGallery.id
          ? {
              ...gallery,
              mediaItems: gallery.mediaItems.map(item =>
                item.id === editingMedia.id
                  ? { ...item, ...updatedFields }
                  : item
              )
            }
          : gallery
      ));

      setSelectedGallery(prev => ({
        ...prev,
        mediaItems: prev.mediaItems.map(item =>
          item.id === editingMedia.id
            ? { ...item, ...updatedFields }
            : item
        )
      }));

      setShowMediaEditor(false);
      setEditingMedia(null);
      console.log('✅ Media updated successfully');
    } catch (error) {
      console.error('❌ Error updating media:', error);
      alert(`Failed to save: ${error.message}`);
    }
  };

  const handleReplaceMedia = async (mediaItem, newFile) => {
    if (!newFile || !mediaItem) return;

    setReplacingProgress(0);
    try {
      const fileType = newFile.type.startsWith('image/') ? 'image' : 'video';
      
      // Use existing uploadMediaFiles method
      const uploadedFiles = await albumService.uploadMediaFiles(
        bookId,
        selectedGallery.id,
        [newFile],
        (current, total) => {
          setReplacingProgress(Math.round((current / total) * 100));
        }
      );

      if (!uploadedFiles || uploadedFiles.length === 0) {
        throw new Error('Upload failed');
      }

      const uploadResult = uploadedFiles[0];

      // Update existing media document with new file data
      await albumService.updateMediaMetadata(
        bookId,
        selectedGallery.id,
        mediaItem.id,
        {
          url: uploadResult.url,
          fileName: uploadResult.fileName,
          originalName: uploadResult.originalName || newFile.name,
          size: uploadResult.size || newFile.size,
          mimeType: uploadResult.mimeType || newFile.type,
          type: fileType,
          replacedAt: new Date().toISOString()
        }
      );

      // Reload galleries to get fresh data
      await loadGalleries();

      setShowReplaceModal(false);
      setReplacingMedia(null);
      setReplacingFile(null);
      console.log('✅ Media replaced successfully');
    } catch (error) {
      console.error('❌ Error replacing media:', error);
      alert(`Failed to replace media: ${error.message}`);
    } finally {
      setReplacingProgress(0);
    }
  };

  // Filter media items based on showHidden state
  const displayedMediaItems = selectedGallery?.mediaItems?.filter(item => 
    showHidden ? true : item.visible !== false
  ) || [];

  if (!isOpen) return null;

return (
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => {
        setActiveId(e.active.id);
        setActiveGalleryId(e.active.id);
      }}
      onDragEnd={(e) => {
        if (e.active.data.current?.type === 'media') {
          handleDragEnd(e);
        } else {
          handleGalleryDragEnd(e);
        }
      }}
      onDragCancel={() => {
        setActiveId(null);
        setActiveGalleryId(null);
      }}
    >
    <div className="bg-white w-full h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="sm:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Gallery Manager</h2>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            {pendingCount > 0 && (
              <button
                onClick={() => setShowApprovalModal(true)}
                className="relative px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors flex items-center gap-2"
              >
                <AlertCircle className="h-4 w-4" />
                <span className="hidden sm:inline text-sm font-medium">Review {pendingCount}</span>
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-88px)]">
          {/* Sidebar with Galleries */}
          <div className={`${isSidebarOpen ? 'block' : 'hidden'} sm:flex w-full sm:w-80 bg-gray-50 border-r border-gray-200 flex-col absolute sm:relative inset-0 sm:inset-auto z-10 sm:z-0 overflow-hidden`}>
            <div className="sm:hidden p-4 border-b border-gray-200 flex items-center justify-between bg-white flex-shrink-0">
              <h3 className="font-semibold text-gray-900">Galleries</h3>
              <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-gray-200 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-3 flex-shrink-0">
              <button
                onClick={() => setShowCreateGallery(!showCreateGallery)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="h-5 w-5" />
                New Gallery
              </button>

              {showCreateGallery && (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Gallery name..."
                    value={newGalleryName}
                    onChange={(e) => setNewGalleryName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && createGallery()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={createGallery}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Create
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateGallery(false);
                        setNewGalleryName('');
                      }}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <SortableContext items={galleries.map(g => g.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {galleries.map((gallery) => (
                    <DroppableGalleryArea key={gallery.id} gallery={gallery} isActive={!!activeId}>
                      <SortableGalleryItem
                        gallery={gallery}
                        selectedGallery={selectedGallery}
                        editingGalleryId={editingGalleryId}
                        editingGalleryName={editingGalleryName}
                        setEditingGalleryName={setEditingGalleryName}
                        setEditingGalleryId={setEditingGalleryId}
                        setSelectedGallery={setSelectedGallery}
                        saveGalleryName={saveGalleryName}
                        openLayoutSettings={openLayoutSettings}
                        updateGalleryVoicePermissions={updateGalleryVoicePermissions}
                        bookData={bookData}
                      />
                    </DroppableGalleryArea>
                  ))}
                </div>
              </SortableContext>
            </div>
          </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6">
            {selectedGallery ? (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                        {selectedGallery.name}
                      </h3>
                      <span className="text-sm text-gray-500">
                        {displayedMediaItems.length} 
                        {showHidden ? ' items' : ' visible items'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs sm:text-sm text-gray-600">Layout:</span>
                      <span className="text-xs sm:text-sm font-medium text-blue-600">
                        {LAYOUT_CATEGORIES[selectedGallery.layoutConfig?.category || 'grid']?.layouts[selectedGallery.layoutConfig?.layout || 'masonry']?.name || 'Masonry'}
                      </span>
                      <button
                        onClick={() => openLayoutSettings(selectedGallery)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Wand2 className="h-4 w-4" />
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openGalleryEditor(selectedGallery);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-all"
                        title="Gallery Settings"
                      >
                        <Settings className="h-4 w-4 text-purple-600" />
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => openGalleryEditor(selectedGallery)}
                      className="px-4 py-2 text-purple-600 hover:bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <Settings className="h-4 w-4" />
                      <span className="hidden sm:inline">Gallery Settings</span>
                    </button>
                    <button
                      onClick={() => setShowHidden(!showHidden)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                        showHidden 
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                      }`}
                    >
                      {showHidden ? (
                        <>
                          <Eye className="h-4 w-4" />
                          <span className="hidden sm:inline">Showing All</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-4 w-4" />
                          <span className="hidden sm:inline">Hiding {selectedGallery.mediaItems?.filter(i => i.visible === false).length || 0} Items</span>
                          <span className="sm:hidden">{selectedGallery.mediaItems?.filter(i => i.visible === false).length || 0}</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => openLayoutSettings(selectedGallery)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Wand2 className="h-4 w-4" />
                      <span className="hidden sm:inline">Configure Layout</span>
                    </button>
                    
                    <div className="relative">
                      <input
                        type="file"
                        id="media-upload"
                        multiple
                        accept="image/*,video/*"
                        onChange={(e) => handleFileUpload(selectedGallery.id, e.target.files)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        disabled={uploading}
                      />
                      <button
                        className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 flex items-center gap-2 disabled:opacity-50"
                        disabled={uploading}
                      >
                        <Upload className="h-4 w-4" />
                        {uploading ? `Uploading... ${uploadProgress}%` : <span className="hidden sm:inline">Upload Media</span>}
                        {!uploading && <span className="sm:hidden">Upload</span>}
                      </button>
                    </div>
                  </div>
                </div>

                {/* YouTube URL Input Section */}
                <div className="mb-6 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center flex-shrink-0">
                      <div className="w-0 h-0 border-l-3 border-l-white border-y-3 border-y-transparent ml-0.5" />
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Add YouTube Video</h4>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="url"
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && youtubeUrl.trim()) {
                          handleAddYoutubeVideo();
                        }
                      }}
                      placeholder="Paste YouTube URL (e.g., https://www.youtube.com/watch?v=...)"
                      className="flex-1 px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      disabled={addingYoutubeVideo}
                    />
                    <button
                      onClick={handleAddYoutubeVideo}
                      disabled={!youtubeUrl.trim() || addingYoutubeVideo}
                      className="w-full sm:w-auto px-6 py-2.5 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium whitespace-nowrap"
                    >
                      {addingYoutubeVideo ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4" />
                          Add Video
                        </>
                      )}
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-600 mt-2">
                    Supported formats: Standard YouTube URLs, shortened youtu.be links, and embed URLs
                  </p>
                </div>

                {/* Media Grid with Drag and Drop */}
                {selectedGallery.mediaItems?.length > 0 ? (
                    <SortableContext
                      items={selectedGallery.mediaItems.map(item => item.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                        {selectedGallery.mediaItems.map((mediaItem) => (
                          <SortableMediaItem
                            key={mediaItem.id}
                            mediaItem={mediaItem}
                            bookId={bookId}
                            selectedGallery={selectedGallery}
                            setReplacingMedia={setReplacingMedia}
                            setShowReplaceModal={setShowReplaceModal}
                            albumService={albumService}
                            setGalleries={setGalleries}
                            setSelectedGallery={setSelectedGallery}
                          />
                        ))}
                      </div>
                    </SortableContext>
                    
                ) : (
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-sm font-medium text-gray-900 mb-2">
                      No media in this gallery
                    </h3>
                    <p className="text-sm text-gray-500 mb-4 px-4">
                      Upload photos/videos or drag files from your file manager
                    </p>
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                      <Upload className="h-4 w-4" />
                      <span className="hidden sm:inline">Drag and drop files anywhere on this page</span>
                      <span className="sm:hidden">Drag files here</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <Image className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Select a Gallery
                </h3>
                <p className="text-gray-500 px-4">
                  Choose a gallery from the sidebar to manage its layout and content
                </p>
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="sm:hidden mt-4 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Show Galleries
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gallery Editor Modal */}
      {showGalleryEditor && editingGallerySettings && (
        <GalleryEditorModal
          user={user}
          gallery={editingGallerySettings}
          albumSettings={bookData?.albumSettings || {}}
          onSave={handleSaveGallerySettings}
          onClose={() => {
            setShowGalleryEditor(false);
            setEditingGallerySettings(null);
          }}
        />
      )}

      {/* Layout Settings Modal */}
      {showLayoutSettings && editingGallery && (
        <GalleryLayoutModal
          gallery={editingGallery}
          layoutConfig={layoutConfig}
          onLayoutChange={setLayoutConfig}
          onSave={(newConfig) => {
            updateGalleryLayout(editingGallery.id, newConfig);
            setShowLayoutSettings(false);
          }}
          onClose={() => setShowLayoutSettings(false)}
        />
      )}

      {showReplaceModal && replacingMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold">Replace Media</h3>
                <p className="text-blue-100 text-sm mt-1">{replacingMedia.originalName || 'Media Item'}</p>
              </div>
              <button
                onClick={() => {
                  setShowReplaceModal(false);
                  setReplacingMedia(null);
                  setReplacingFile(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Current Media Preview */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Media</label>
                <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  {replacingMedia.type === 'image' ? (
                    <img src={replacingMedia.url} alt="Current" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload New {replacingMedia.type === 'image' ? 'Image' : 'Video'}
                </label>
                <input
                  type="file"
                  accept={replacingMedia.type === 'image' ? 'image/*' : 'video/*'}
                  onChange={(e) => setReplacingFile(e.target.files[0])}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {replacingFile && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: {replacingFile.name} ({(replacingFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              {/* Progress Bar */}
              {replacingProgress > 0 && (
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${replacingProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-1 text-center">{replacingProgress}%</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    if (!replacingFile) {
                      alert('Please select a file first');
                      return;
                    }
                    handleReplaceMedia(replacingMedia, replacingFile);
                  }}
                  disabled={!replacingFile || replacingProgress > 0}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                >
                  {replacingProgress > 0 ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Replacing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5" />
                      Replace Media
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowReplaceModal(false);
                    setReplacingMedia(null);
                    setReplacingFile(null);
                  }}
                  disabled={replacingProgress > 0}
                  className="px-6 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMediaEditor && editingMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-semibold">Edit Media</h3>
                <p className="text-blue-100 text-sm mt-1">{editingMedia.title || editingMedia.originalName || 'Media Item'}</p>
              </div>
              <button
                onClick={() => {
                  setShowMediaEditor(false);
                  setEditingMedia(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <MediaEditForm
                mediaItem={editingMedia}
                onSave={handleSaveMediaEdit}
                onCancel={() => {
                  setShowMediaEditor(false);
                  setEditingMedia(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showApprovalModal && (
        <MediaApprovalModal
          bookId={bookId}
          onClose={() => setShowApprovalModal(false)}
          onApprovalComplete={() => {
            loadGalleries();
            countPendingMedia();
          }}
        />
      )}
    </div>

    <DragOverlay>
      {(activeId || activeGalleryId) ? (
        <div className="bg-white rounded-lg shadow-2xl border-2 border-pink-400 opacity-90 p-3">
          <div className="w-32 h-32 bg-gradient-to-br from-pink-100 to-purple-100 rounded flex items-center justify-center">
            <GripVertical className="h-8 w-8 text-pink-400" />
          </div>
        </div>
      ) : null}
    </DragOverlay>

    {/* Close DndContext */}
    </DndContext>
  </div>
);
};